import {
  BasePaginateParamsSchema,
  BasePaginateResultSchema,
} from '@/api/common.ts'
import { z } from 'zod'

export enum QcHoldResultType {
  Hold,
  SortingOrRework,
  Rejected,
}

export enum QcHoldType {
  RawMaterial,
  RawMaterialInProcess,
  FinishedGoods,
  InProcess,
  FinishedGoodsInProduction,
}

export enum QcHoldDocumentStatus {
  UnExpected = 0,
  Draft = 1,
  Posted = 2,
  Completed = 3,
  Cancelled = 4,
}

export enum PlanningAcknowledgmentStatus {
  Pending,
  Acknowledged,
  Bypassed,
}

export const Qc8010SearchParamsSchema = z
  .object({
    number: z.string().optional(),
    itemId: z.string().optional(),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
    type: z.string().optional(),
    status: z
      .union([z.literal('1'), z.literal('2'), z.literal('3'), z.literal('4')])
      .optional(),
    isReworkFromRejection: z
      .union([z.literal('true'), z.literal('false'), z.boolean()])
      .optional()
      .transform((v) => {
        if (v === true || v === 'true') return true
        if (v === false || v === 'false') return false
        return undefined
      }),
    // Search filters
    lotNo: z.string().optional(),
    lotDateFrom: z.coerce.date().optional(),
    lotDateTo: z.coerce.date().optional(),
    mold: z.string().optional(),
    // Checkbox filters
    isClaimItem: z.coerce.boolean().optional(),
    plnNotAcknowledged: z.coerce.boolean().optional(),
    prdSortingCompleted: z.coerce.boolean().optional(),
    prdNoWithdrawal: z.coerce.boolean().optional(),
    prdNoSortingResult: z.coerce.boolean().optional(),
  })
  .merge(BasePaginateParamsSchema)

export type Qc8010SearchParams = z.infer<typeof Qc8010SearchParamsSchema>

export const HoldResultSchema = z.object({
  byName: z.string(),
  date: z.coerce.date().nullable(),
  qtyPassed: z.number(),
  qtyRejected: z.number(),
  qtySortingRework: z.number(),
  total: z.number(),
  isNeedSortingReworkResult: z.boolean(),
  remark: z.string(),
})

export type HoldResultModel = z.infer<typeof HoldResultSchema>

export const SortingReworkResultSchema = z.object({
  byName: z.string(),
  date: z.coerce.date().nullable(),
  qtyPassed: z.number(),
  qtyRejected: z.number(),
  remark: z.string(),
  shift: z.number().nullish(),
})

export type Qc8010SortingReworkResultModel = z.infer<
  typeof SortingReworkResultSchema
>

export const Qc8010WorkResultSchema = z.object({
  id: z.number(),
  confirmSortingResult: z.object({
    pass: z.boolean(),
    date: z.coerce.date().nullable(),
  }),
  sortingReworkResult: SortingReworkResultSchema,
})

export type Qc8010WorkResultModel = z.infer<typeof Qc8010WorkResultSchema>

export const Qc8010SearchResultModelSchema = z
  .object({
    id: z.string(),
    number: z.string(),
    date: z.coerce.date(),
    item: z.object({
      itemId: z.number(),
      lotNo: z.string(),
      lotDate: z.coerce.date(),
      mold: z.string(),
      unit: z.string(),
      isClaimItem: z.boolean(),
    }),
    qty: z.number(),
    type: z.nativeEnum(QcHoldType),
    holdResult: HoldResultSchema,
    sortingReworkResult: SortingReworkResultSchema,
    problem: z.object({
      isRawMaterialProblem: z.boolean(),
      isComponentProblem: z.boolean(),
      appearances: z.array(z.string()).optional(),
      functionalities: z.array(z.string()).optional(),
      appearance: z.string(),
      functionality: z.string(),
      note: z.string().optional(),
    }),
    createdAt: z.coerce.date(),
    status: z.nativeEnum(QcHoldDocumentStatus).nullable(),
    workResults: z.array(Qc8010WorkResultSchema),
    documentType: z.enum(['MRR', 'NCR']).nullable().optional(),
    planningAcknowledgmentStatus: z
      .nativeEnum(PlanningAcknowledgmentStatus)
      .optional(),
    isCancellationAllowed: z.boolean().optional(),
    isReworkFromRejection: z.boolean().optional(),
    sourceQcHoldId: z.string().uuid().nullable().optional(),
    itemLocation: z.string().nullable().optional(),
  })
  .transform((v) => ({
    ...v,
    qtySortingRework: v.holdResult.qtySortingRework,
    sumWorkResultQty: v.workResults
      .filter(
        (x) => !x.confirmSortingResult.date || x.confirmSortingResult.pass
      )
      .reduce(
        (a, b) =>
          a +
          b.sortingReworkResult.qtyPassed +
          b.sortingReworkResult.qtyRejected,
        0
      ),
  }))

export type Qc8010SearchResultModel = z.infer<
  typeof Qc8010SearchResultModelSchema
>

export const Qc8010SearchResultSchema = z
  .object({
    items: z.array(Qc8010SearchResultModelSchema),
  })
  .merge(BasePaginateResultSchema)

export type Qc8010SearchResult = z.infer<typeof Qc8010SearchResultSchema>
